<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import type {
  CategoryFilter,
  EventFiltersCategoryFilters,
} from "mws-ts-v2-sdk";

import type { FilterButtonClickedPageType as PageType } from "~/analytics/segment";
import { queries } from "~/queries";

type Props =
  | {
      type: "event";
      searchTerm?: never;
      categoryIdentifier?: never;
      eventIdentifier: string;
      isDirectlyVisible?: boolean;
    }
  | {
      type: "search";
      categoryIdentifier?: never;
      eventIdentifier?: never;
      searchTerm: string;
      isDirectlyVisible?: boolean;
    }
  | {
      type: "category";
      eventIdentifier?: never;
      searchTerm?: never;
      categoryIdentifier: string;
      isDirectlyVisible?: boolean;
    };

const props = defineProps<Props>();
const type = computed(() => props.type);
const pageType = computed<PageType>(() => {
  if (type.value === "search") return "searchProductsPage";

  if (type.value === "category") return "categoryPage";

  return "eventDetailsPage";
});

const filterParams = computed(() => ({
  eventIdentifier: props.eventIdentifier,
  searchTerm: props.searchTerm,
  categoryIdentifier: props.categoryIdentifier,
}));

const route = useRoute();
const router = useRouter();
const { filtersCleared } = useAnalytics();

const platformStore = usePlatformStore();
const platformId = computed(
  () => platformStore.getPlatformConfiguration.platformId,
);
const isAuctionsEnabled = computed(
  () => platformStore.getPlatformConfiguration.isAuctionsEnabled,
);

const { data: filters, isLoading } = useQuery({
  ...queries.products.filters(platformId, filterParams),
  staleTime: Infinity,
  select: (filters) => {
    const categoryFilters: Partial<EventFiltersCategoryFilters> =
      filters.categoryFilters;

    // Remove condition categories for platforms where that doesn't matter
    if (!isAuctionsEnabled.value) {
      delete categoryFilters.condition;
    }

    const mappedCategoryFilters = Object.fromEntries(
      Object.entries(categoryFilters).map((entry) => {
        const [key, values] = entry as [string, CategoryFilter[]];

        return [
          key,
          values.map((value) => ({
            label: value.name,
            value: value.id,
          })),
        ];
      }),
    );

    return {
      ...filters,
      categoryFilters: mappedCategoryFilters,
    };
  },
});

const isSheetOpen = ref(false);

const onClearFilters = () => {
  router.replace({
    ...route,
    query: {},
  });
  window.scrollTo(0, 0);

  filtersCleared({ page_type: pageType.value });
  isSheetOpen.value = false;
};

const filterCount = computed(() => {
  const priceCount = route.query.priceFrom || route.query.priceTo ? 1 : 0;
  const statusCount = route.query.productStatus ? 1 : 0;
  let categoriesCount = 0;

  if (route.query.category) {
    categoriesCount = Array.isArray(route.query.category)
      ? route.query.category.length
      : 1;
  }

  const sortingApplied = transformRouteParams(route.query.sort, "string")
    ? 1
    : 0;

  return (
    categoriesCount + priceCount + statusCount + sortingApplied || undefined
  );
});
</script>

<template>
  <template v-if="isDirectlyVisible">
    <div>
      <ProductFilterPillContainer @clear-filters="onClearFilters" />

      <ProductFilterSections
        :is-auctions-enabled="isAuctionsEnabled"
        :type="type"
        :available-filters="filters"
      />
    </div>
  </template>
  <template v-else>
    <ProductFilterButtonWithSheetMenu
      v-model:is-open="isSheetOpen"
      :filters-count="filterCount"
      :type="pageType"
      :is-loading="isLoading"
      :class="{
        'md:hidden': searchTerm,
        'md:block': categoryIdentifier || eventIdentifier,
      }"
    >
      <ProductFilterSections
        :is-auctions-enabled="isAuctionsEnabled"
        :type="type"
        :available-filters="filters"
      />
      <template #actions>
        <BaseFilterControlButtons
          :is-disabled="isLoading"
          @clear-filters="onClearFilters"
          @apply-filters="isSheetOpen = false"
        />
      </template>
    </ProductFilterButtonWithSheetMenu>
  </template>
</template>
